import type { Consultation } from "@/types/api/consultation";

export type Patient = {
  id: number;
  doctorId: number;
  lastName: string;
  firstName: string;
  gender: Gender;
  birthDate: Date;
  email: string;
  mobilePhone?: string;
  homePhone?: string;
  job?: string;
  address?: string;
  addressComplement?: string;
  postalCode?: number;
  city?: string;
  consultationMotive: ConsultationReason;
  toothBrush?: ToothBrushType;
  toothBrushHair?: ToothBrushHairType;
  brushingFrequency?: string;
  interdentalSystem: boolean;
  brushingAccessories?: string;
  toothPaste?: string;
  mouthWash?: string;
  toothState: GravityState;
  smoking: SmokingPatient;
  dailySmokes?: number;
  smokingEnd?: Date;
  smokingState: GravityState;
  diabetic: DiabeticPatient;
  hba1c?: number;
  diabetComment?: string;
  diabetState: GravityState;
  geneticTendency: boolean;
  geneticComment?: string;
  geneticState: GravityState;
  stress: number;
  stressState: GravityState;
  stressComment?: string;
  associatedPathologies: Array<Pathology>;
  pathologyComment?: string;
  allergies: Array<Allergy>;
  allergiesComment?: string;
  womenHealth: Array<WomenHealth>;
  pregnancyEnd?: Date;
  womenHealthComment?: string;
  consultations: Array<Consultation>;
  lastConsultationDate?: Date;
  sportsComment?: string;
  dietComment?: string;
  disease: Disease;
  diseaseState: GravityState;
  createdAt: Date;
  updatedAt: Date;
  code?: string | null;
  patientComment?: string;
  plaqueIndex?: number;
  bleedingIndex?: number;
  stateGradeAbscess?: string;
  consultationMotiveComment?: string;
};

export type ConsultationReason =
  | "BLEEDING"
  | "MOBILITY"
  | "PAIN"
  | "HALITOSE"
  | "AESTHETIC"
  | "CONTROL"
  | "ADRESSED";

export type Gender = "MALE" | "FEMALE" | "OTHER";

export type GravityState = "UNSTABLE" | "TREATMENT" | "STABLE" | "NONE";

export type ToothBrushType = "MANUAL" | "ELECTRICAL" | "BOTH";

export type ToothBrushHairType = "SOFT" | "MEDIUM" | "HARD";

export type Pathology =
  | "SYNDROME"
  | "HEART"
  | "KIDNEY"
  | "IMMUNE"
  | "OBESITY"
  | "RESPIRATORY"
  | "POLYARTHRITIS"
  | "ALZHEIMER"
  | "OTHER";

export type Allergy = "PENICILLIN" | "CODEIN" | "POVIDONE" | "OTHER";

export type WomenHealth =
  | "MENOPAUSE"
  | "PREGNANT"
  | "ENDOMETRIOSIS"
  | "SOPK"
  | "OTHER";

export type SmokingPatient = "TRUE" | "FALSE" | "PREVIOUS";

export type DiabeticPatient = "NONE" | "ONE" | "TWO" | "GESTATIONAL";

export type Disease = "GINGIVITIS" | "PERIODONTITIS" | "PERIIMPLANTITIS";

export const Genders = ["FEMALE", "MALE", "OTHER"] as const;

export const ConsultationReasons = [
  "BLEEDING",
  "MOBILITY",
  "PAIN",
  "HALITOSE",
  "AESTHETIC",
  "CONTROL",
  "ADRESSED",
] as const;

export const GravityStates = [
  "UNSTABLE",
  "TREATMENT",
  "STABLE",
  "NONE",
] as const;

export const ToothBrushTypes = ["MANUAL", "ELECTRICAL", "BOTH"] as const;

export const ToothBrushHairTypes = ["SOFT", "MEDIUM", "HARD"] as const;

export const SmokingPatient = ["TRUE", "FALSE", "PREVIOUS"] as const;

export const DiabeticPatient = ["NONE", "ONE", "TWO", "GESTATIONAL"] as const;

export const Pathologies = [
  "SYNDROME",
  "HEART",
  "KIDNEY",
  "IMMUNE",
  "OBESITY",
  "RESPIRATORY",
  "POLYARTHRITIS",
  "ALZHEIMER",
  "OTHER",
] as const;

export const Allergies = ["PENICILLIN", "CODEIN", "POVIDONE", "OTHER"] as const;

export const WomenHealth = [
  "PREGNANT",
  "MENOPAUSE",
  "SOPK",
  "ENDOMETRIOSIS",
  "OTHER",
] as const;

export const Diseases = [
  "GINGIVITIS",
  "PERIODONTITIS",
  "PERIIMPLANTITIS",
] as const;

export type Percentage = {
  diseaseState: GravityState;
  round: string;
};
